import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import { io, Socket } from 'socket.io-client';
import socketServiceManager from 'app/socketService';

// ----------- Type Definitions -----------
interface SocketEvent {
  event: string;
  data: any;
  timestamp: number;
}

interface SocketContextType {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;

  // Socket instance
  socket: Socket | null;

  // Connection management
  connect: (url?: string, options?: any) => void;
  disconnect: () => void;
  reconnect: () => void;

  // Event handling
  emit: (event: string, data?: any) => void;
  on: (event: string, callback: (data: any) => void) => void;
  off: (event: string, callback?: (data: any) => void) => void;

  // Event history
  eventHistory: SocketEvent[];
  clearEventHistory: () => void;

  // Connection info
  connectionUrl: string | null;
  lastConnectedAt: Date | null;
  lastDisconnectedAt: Date | null;
}

// ----------- Context Create -----------
export const SocketContext = createContext<SocketContextType>({
  isConnected: false,
  isConnecting: false,
  connectionError: null,
  socket: null,
  connect: () => {},
  disconnect: () => {},
  reconnect: () => {},
  emit: () => {},
  on: () => {},
  off: () => {},
  eventHistory: [],
  clearEventHistory: () => {},
  connectionUrl: null,
  lastConnectedAt: null,
  lastDisconnectedAt: null,
});

// ----------- Provider Component -----------
export const SocketProvider: React.FC<{
  children: ReactNode;
  defaultUrl?: string;
  defaultOptions?: any;
}> = ({ children, defaultUrl, defaultOptions = {} }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [eventHistory, setEventHistory] = useState<SocketEvent[]>([]);
  const [connectionUrl, setConnectionUrl] = useState<string | null>(
    defaultUrl || null,
  );
  const [lastConnectedAt, setLastConnectedAt] = useState<Date | null>(null);
  const [lastDisconnectedAt, setLastDisconnectedAt] = useState<Date | null>(
    null,
  );

  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 1000; // Start with 1 second

  // Add event to history
  const addEventToHistory = useCallback((event: string, data: any) => {
    const socketEvent: SocketEvent = {
      event,
      data,
      timestamp: Date.now(),
    };
    setEventHistory(prev => [...prev.slice(-99), socketEvent]); // Keep last 100 events
  }, []);

  // Clear event history
  const clearEventHistory = useCallback(() => {
    setEventHistory([]);
  }, []);

  // Handle connection events
  const handleConnect = useCallback(() => {
    console.log('Socket.IO: Connected successfully');
    setIsConnected(true);
    setIsConnecting(false);
    setConnectionError(null);
    setLastConnectedAt(new Date());
    reconnectAttemptsRef.current = 0;
    addEventToHistory('connect', { timestamp: new Date().toISOString() });
  }, [addEventToHistory]);

  const handleDisconnect = useCallback(
    (reason: string) => {
      console.log('Socket.IO: Disconnected', reason);
      setIsConnected(false);
      setIsConnecting(false);
      setLastDisconnectedAt(new Date());
      addEventToHistory('disconnect', {
        reason,
        timestamp: new Date().toISOString(),
      });

      // Auto-reconnect logic
      if (
        reason === 'io server disconnect' ||
        reason === 'io client disconnect'
      ) {
        // Server or client initiated disconnect - don't auto-reconnect
        return;
      }

      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current++;
        const delay =
          reconnectDelay * Math.pow(2, reconnectAttemptsRef.current - 1); // Exponential backoff

        console.log(
          `Socket.IO: Attempting to reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`,
        );

        reconnectTimeoutRef.current = setTimeout(() => {
          if (socketRef.current) {
            socketRef.current.connect();
          }
        }, delay);
      } else {
        setConnectionError(
          `Failed to reconnect after ${maxReconnectAttempts} attempts`,
        );
      }
    },
    [addEventToHistory],
  );

  const handleConnectError = useCallback(
    (error: Error) => {
      console.error('Socket.IO: Connection error', error);
      setIsConnecting(false);
      setConnectionError(error.message);
      addEventToHistory('connect_error', {
        error: error.message,
        timestamp: new Date().toISOString(),
      });
    },
    [addEventToHistory],
  );

  // Connect function - uses global socket from socketServiceManager
  const connect = useCallback(
    (url?: string, options: any = {}) => {
      const socketUrl = url || defaultUrl;
      if (!socketUrl) {
        setConnectionError('No socket URL provided');
        return;
      }

      setIsConnecting(true);
      setConnectionError(null);
      setConnectionUrl(socketUrl);

      const socketConfig = {
        url: socketUrl,
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        options: {
          ...defaultOptions,
          ...options,
        }
      };

      console.log('Socket.IO: Connecting to', socketUrl, '(using global socket)');

      // Use global socket from socketServiceManager instead of creating new one
      const globalSocket = socketServiceManager.getGlobalSocket();
      
      if (globalSocket) {
        // Use existing global socket
        socketRef.current = globalSocket;
        setIsConnecting(false);
        setIsConnected(globalSocket.connected);
        
        // Set up event listeners
        globalSocket.on('connect', handleConnect);
        globalSocket.on('disconnect', handleDisconnect);
        globalSocket.on('connect_error', handleConnectError);

        // Listen for all events and add to history
        globalSocket.onAny((eventName: string, ...args: any[]) => {
          addEventToHistory(eventName, args);
        });
      } else {
        // Initialize global socket if it doesn't exist
        socketServiceManager.initializeGlobalSocket(socketConfig);
        
        // Wait a bit for the global socket to be created
        setTimeout(() => {
          const newGlobalSocket = socketServiceManager.getGlobalSocket();
          if (newGlobalSocket) {
            socketRef.current = newGlobalSocket;
            setIsConnecting(false);
            setIsConnected(newGlobalSocket.connected);
            
            // Set up event listeners
            newGlobalSocket.on('connect', handleConnect);
            newGlobalSocket.on('disconnect', handleDisconnect);
            newGlobalSocket.on('connect_error', handleConnectError);

            // Listen for all events and add to history
            newGlobalSocket.onAny((eventName: string, ...args: any[]) => {
              addEventToHistory(eventName, args);
            });
          } else {
            setIsConnecting(false);
            setConnectionError('Failed to initialize global socket');
          }
        }, 100);
      }
    },
    [
      defaultUrl,
      defaultOptions,
      handleConnect,
      handleDisconnect,
      handleConnectError,
      addEventToHistory,
    ],
  );

  // Disconnect function - don't disconnect global socket, just remove listeners
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      console.log('Socket.IO: Removing event listeners (global socket remains connected)');
      // Remove event listeners but don't disconnect the global socket
      socketRef.current.off('connect', handleConnect);
      socketRef.current.off('disconnect', handleDisconnect);
      socketRef.current.off('connect_error', handleConnectError);
      socketRef.current.offAny();
      socketRef.current = null;
    }

    setIsConnected(false);
    setIsConnecting(false);
    reconnectAttemptsRef.current = 0;
  }, [handleConnect, handleDisconnect, handleConnectError]);

  // Reconnect function
  const reconnect = useCallback(() => {
    if (socketRef.current && connectionUrl) {
      disconnect();
      setTimeout(() => {
        connect(connectionUrl);
      }, 100);
    }
  }, [disconnect, connect, connectionUrl]);

  // Emit function
  const emit = useCallback(
    (event: string, data?: any) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit(event, data);
        addEventToHistory(`emit:${event}`, data);
      } else {
        console.warn('Socket.IO: Cannot emit event - not connected');
      }
    },
    [isConnected, addEventToHistory],
  );

  // On function
  const on = useCallback((event: string, callback: (data: any) => void) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback);
    }
  }, []);

  // Off function
  const off = useCallback((event: string, callback?: (data: any) => void) => {
    if (socketRef.current) {
      if (callback) {
        socketRef.current.off(event, callback);
      } else {
        socketRef.current.off(event);
      }
    }
  }, []);

  // Sync with global socket connection state
  useEffect(() => {
    const globalSocket = socketServiceManager.getGlobalSocket();
    if (globalSocket) {
      socketRef.current = globalSocket;
      
      // Sync connection state
      setIsConnected(globalSocket.connected);
      
      // Set up event listeners (Socket.IO allows multiple listeners)
      globalSocket.on('connect', handleConnect);
      globalSocket.on('disconnect', handleDisconnect);
      globalSocket.on('connect_error', handleConnectError);
      globalSocket.onAny((eventName: string, ...args: any[]) => {
        addEventToHistory(eventName, args);
      });
    }
    
    // Check connection state periodically
    const interval = setInterval(() => {
      const currentGlobalSocket = socketServiceManager.getGlobalSocket();
      if (currentGlobalSocket) {
        setIsConnected(currentGlobalSocket.connected);
      }
    }, 1000);
    
    return () => {
      clearInterval(interval);
      // Don't disconnect global socket, just remove our listeners
      if (socketRef.current) {
        socketRef.current.off('connect', handleConnect);
        socketRef.current.off('disconnect', handleDisconnect);
        socketRef.current.off('connect_error', handleConnectError);
      }
    };
  }, [handleConnect, handleDisconnect, handleConnectError, addEventToHistory]);

  const contextValue: SocketContextType = {
    isConnected,
    isConnecting,
    connectionError,
    socket: socketRef.current,
    connect,
    disconnect,
    reconnect,
    emit,
    on,
    off,
    eventHistory,
    clearEventHistory,
    connectionUrl,
    lastConnectedAt,
    lastDisconnectedAt,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

// ----------- Custom Hook -----------
export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

// ----------- Utility Hooks -----------
export const useSocketEvent = (
  event: string,
  callback: (data: any) => void,
) => {
  const { on, off } = useSocket();

  useEffect(() => {
    on(event, callback);
    return () => off(event, callback);
  }, [event, callback, on, off]);
};

export const useSocketConnection = () => {
  const {
    isConnected,
    isConnecting,
    connectionError,
    connect,
    disconnect,
    reconnect,
  } = useSocket();
  return {
    isConnected,
    isConnecting,
    connectionError,
    connect,
    disconnect,
    reconnect,
  };
};
